//#region [IMPORT]

// === THIRD-PARTY ===
import React, { useEffect, useRef, useState } from 'react';

// ### MUI / STYLES ###
import { useTheme } from '@mui/material/styles';

// === COMPONENTS ===
import Collapsible, { Header } from 'components/Collapsible';
import Styled from 'components/Styled/Styled';
import Field  from './components/Field/Field';

// === HOOKS ===
import { useFile } from 'pages/FileViewer/components/FileContext/FileContext';
import useOnVisible from 'components/hooks/useOnVisible/useOnVisible';

// === API ===
import { on, removeListener, sendMessageData } from 'api/prokey/prokey.websocket';

//#endregion


//-----------------------------------------------------------------
export default function Document(props) {
  //-----------------------------------------------------------------

  //#region [VALUES]
 
  const { id, is_comp = false } = props;

  const theme = useTheme();

  const { file_id } = useFile();

  const [ document , setDocument ] = useState([]);
  const [ has_focus, setHasFocus ] = useState(false);
  
  const ref = useRef();
  const [is_visible] = useOnVisible(ref);

  //#endregion
  
  //#region [EFFECT]
  
  useEffect( () => {
    const DOCUMENT = is_comp ? 'FILE_COMPARISON_DOCUMENT' : 'FILE_DOCUMENT';
    
    //-----------------------------------------------------------------
    function handleDocument( data ) {
      //-----------------------------------------------------------------
      if( id !== data.id ) return;
      setDocument( data );
    }

    on( DOCUMENT, handleDocument );

    return () => {
      removeListener( DOCUMENT, handleDocument );
    }
  }, [is_comp] )
  
  useEffect( () => {
    //-----------------------------------------------------------------
    function handleFocus(data) {
      //-----------------------------------------------------------------
      if( id !== data.id && has_focus ) {
        setHasFocus( false );
      }
      if( id !== data.id ) {
        return;
      }
      if( has_focus ) {
        return;
      }
      
      setHasFocus( true );
      ref.current?.scrollIntoView({ behavior: "instant", block: "center", inline: "nearest" });
    }

    const type = is_comp ? 'FILE_COMPARISON_DOCUMENT_FOCUS' : 'FILE_DOCUMENT_FOCUS';
    on( type, handleFocus );

    return () => {
      removeListener( type, handleFocus );
    }
  }, [id, ref, has_focus, is_comp] );
      
  useEffect( () => {
    if( !is_visible ) return;
    const type = is_comp ? 'FILE_REQUEST_COMPARISON_DOCUMENT' : 'FILE_REQUEST_DOCUMENT';
    sendMessageData( type, { file_id, document_id: id } );
  }, [id,is_visible] )

  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    if( !document.is_selected ) return (<div ref={ref}></div>);
    
    const body = displayFields()
    const color = is_comp ? theme.palette.secondary.main : '#ffa500';
    const bg = has_focus ? { background: color } : { background: theme.palette.primary.main }
    return (
      <Collapsible
        key={id}
        styles={ [{height: 'fit-content'}]}
        header        = {{ children: <Header title={document.name} styles={[ {fontWeight: 'bold'} ]} />, styles:[bg, { color: theme.palette.primary.contrastText }] }}
        header_closed = {{ children: <Header title={document.name} styles={[ {fontWeight: 'bold'} ]} />, styles:[bg, { color: theme.palette.primary.contrastText }] }}
        body          = {{
          children: body,
          styles: [{height: '500px'} ]
        }}
        body_closed   = {{ children: null }}
      />
    )
  }

  //-----------------------------------------------------------------
  function displayFields() {
    //-----------------------------------------------------------------
    const fields = document.fields?.map( id => ( <Field key={id} id={id} is_comp={is_comp} /> )) || []
    return (
      <Styled ref={ref} styles={[{overflow:'auto'}]}>
        {fields}
      </Styled>
    );
  }

  //#endregion

  return display();
}